import { useEffect, useId, useMemo, useRef, useState } from "react";
import CountryFlag from "@/components/CountryFlag.jsx";
import { countryDisplayName } from "@/utils/countryDisplay.js";
import { useViewportAnchoredMenu } from "@/hooks/useViewportAnchoredMenu.js";
import "./country-field.css";

function formatCountryLabel(country) {
    return countryDisplayName(country);
}

function matchesQuery(country, query) {
    const normalized = query.trim().toLowerCase();
    if (!normalized) {
        return true;
    }
    const haystack = [
        country.name,
        country.shortName,
        country.isoCode,
        country.isoCode3
    ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
    return haystack.includes(normalized);
}

export default function CountryField({ value, countries, onChange, disabled = false }) {
    const listboxId = useId();
    const containerRef = useRef(null);
    const inputRef = useRef(null);
    const suggestionsRef = useRef(null);
    const selectedCountry = useMemo(
        () => countries.find((country) => country.id === value) || null,
        [countries, value]
    );
    const [query, setQuery] = useState("");
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);

    const filteredCountries = useMemo(
        () => countries.filter((country) => matchesQuery(country, query)),
        [countries, query]
    );

    useViewportAnchoredMenu(open && filteredCountries.length > 0, inputRef, suggestionsRef, {
        preferAlign: "start",
        matchAnchorWidth: true
    });

    useEffect(() => {
        if (!open) {
            setQuery(selectedCountry ? formatCountryLabel(selectedCountry) : "");
        }
    }, [selectedCountry, open]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleSelect = (country) => {
        onChange(country?.id || null);
        setQuery(country ? formatCountryLabel(country) : "");
        setOpen(false);
        setActiveIndex(-1);
    };

    const handleClear = () => {
        onChange(null);
        setQuery("");
        setOpen(false);
        inputRef.current?.focus();
    };

    const handleInputChange = (event) => {
        const nextQuery = event.target.value;
        setQuery(nextQuery);
        setOpen(true);
        setActiveIndex(-1);
        if (selectedCountry && nextQuery !== formatCountryLabel(selectedCountry)) {
            onChange(null);
        }
    };

    const handleKeyDown = (event) => {
        if (event.key === "ArrowDown") {
            event.preventDefault();
            setOpen(true);
            if (filteredCountries.length > 0) {
                setActiveIndex((current) => (current + 1) % filteredCountries.length);
            }
            return;
        }

        if (event.key === "ArrowUp") {
            event.preventDefault();
            if (filteredCountries.length > 0) {
                setActiveIndex((current) => (current <= 0 ? filteredCountries.length - 1 : current - 1));
            }
            return;
        }

        if (event.key === "Enter" && open && activeIndex >= 0) {
            event.preventDefault();
            handleSelect(filteredCountries[activeIndex]);
            return;
        }

        if (event.key === "Escape") {
            setOpen(false);
        }
    };

    return (
        <label className="field country-field">
            Pays d&apos;origine
            <div className="country-field-combobox" ref={containerRef}>
                <div className="country-field-input-wrap">
                    {selectedCountry && !open && (
                        <span className="country-field-selected-flag" aria-hidden="true">
                            <CountryFlag country={selectedCountry} size="sm" />
                        </span>
                    )}
                    <input
                        ref={inputRef}
                        type="search"
                        role="combobox"
                        aria-expanded={open}
                        aria-controls={listboxId}
                        aria-autocomplete="list"
                        placeholder="Rechercher un pays (optionnel)"
                        value={query}
                        onChange={handleInputChange}
                        onFocus={() => setOpen(true)}
                        onKeyDown={handleKeyDown}
                        disabled={disabled}
                        autoComplete="off"
                    />
                    {selectedCountry && (
                        <button
                            type="button"
                            className="country-field-clear"
                            onClick={handleClear}
                            disabled={disabled}
                            aria-label="Retirer le pays d'origine"
                        >
                            ×
                        </button>
                    )}
                </div>
                {open && filteredCountries.length === 0 && query.trim() && (
                    <span className="country-field-status muted">Aucun pays trouvé</span>
                )}
                {open && filteredCountries.length > 0 && (
                    <ul
                        ref={suggestionsRef}
                        id={listboxId}
                        className="country-field-suggestions"
                        role="listbox"
                    >
                        {filteredCountries.map((country, index) => (
                            <li key={country.id}>
                                <button
                                    type="button"
                                    role="option"
                                    aria-selected={index === activeIndex}
                                    className={index === activeIndex ? "is-active" : ""}
                                    onMouseDown={(event) => event.preventDefault()}
                                    onClick={() => handleSelect(country)}
                                >
                                    <span className="country-field-option">
                                        <CountryFlag country={country} size="sm" />
                                        <span className="country-field-option-name">{formatCountryLabel(country)}</span>
                                    </span>
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </label>
    );
}
